import { useState } from 'react'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { useSnackBar } from '../../../providers/snack_bar_provider'

export type PdfOrientation = 'portrait' | 'landscape'

export const useBusinessPdfDownload = () => {
  const { showSnackBar } = useSnackBar()
  const [isDownloading, setIsDownloading] = useState(false)

  const downloadPdf = async (
    projectName: string,
    slideRefs: Array<React.RefObject<HTMLDivElement>>,
    orientation: PdfOrientation
  ): Promise<boolean> => {
    if (slideRefs.length === 0) return false

    setIsDownloading(true)

    // 実際のスライド要素の幅を取得してA4の縦横比に基づいてスライドの高さを計算
    const firstSlideElement = slideRefs[0]?.current
    if (!firstSlideElement) {
      setIsDownloading(false)
      showSnackBar(['スライドの読み込みに失敗しました。'], 'error')
      return false
    }
    const viewportWidth = firstSlideElement.offsetWidth
    const a4Ratio = orientation === 'portrait' ? 297 / 210 : 210 / 297 // A4のアスペクト比
    const slideHeight = Math.floor(viewportWidth * a4Ratio)

    // フッターを最下部に配置するため、PDF生成時のみスタイルを一時適用
    const style = document.createElement('style')
    style.id = 'pdf-generation-styles'
    style.textContent = `
      .slide-container {
        min-height: ${slideHeight}px !important;
      }
      .slide-content {
        padding-bottom: 80px !important;
      }
      .slide-footer {
        position: absolute !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        padding-bottom: 1.5rem !important;
        padding-left: 2rem !important;
        padding-right: 2rem !important;
        margin-top: 0 !important;
      }
    `
    document.head.appendChild(style)

    try {
      // A4サイズのPDFを作成（向きは指定されたものを使用）
      const pdf = new jsPDF({
        orientation: orientation,
        unit: 'mm',
        format: 'a4',
      })

      // A4サイズ（mm）
      const pdfWidth = orientation === 'landscape' ? 297 : 210
      const pdfHeight = orientation === 'landscape' ? 210 : 297

      // 各スライドをPDFに追加
      for (let i = 0; i < slideRefs.length; i++) {
        const slideElement = slideRefs[i].current
        if (!slideElement) continue

        // html2canvasでスライドをキャプチャ
        const canvas = await html2canvas(slideElement, {
          scale: 2, // 高品質化のため2倍のスケール
          useCORS: true, // 外部画像の読み込みを許可
          logging: false, // ログ出力を無効化
        })

        // canvasを画像データに変換
        const imgData = canvas.toDataURL('image/png')

        // 画像のアスペクト比を計算
        const imgWidth = canvas.width
        const imgHeight = canvas.height
        const imageRatio = imgWidth / imgHeight

        // PDFページのサイズに合わせて画像サイズを調整
        let finalPdfWidth = pdfWidth
        let finalPdfHeight = pdfWidth / imageRatio

        // 高さがページをはみ出す場合は高さを基準に調整
        if (finalPdfHeight > pdfHeight) {
          finalPdfHeight = pdfHeight
          finalPdfWidth = pdfHeight * imageRatio
        }

        // 画像を中央配置するための座標を計算
        const x = (pdfWidth - finalPdfWidth) / 2
        const y = 0

        // 2ページ目以降は新しいページを追加
        if (i > 0) {
          pdf.addPage()
        }

        // PDFに画像を追加
        pdf.addImage(imgData, 'PNG', x, y, finalPdfWidth, finalPdfHeight)
      }

      // PDFをダウンロード
      pdf.save(`${projectName}.pdf`)

      showSnackBar(['PDFをダウンロードしました'], 'success')
      return true
    } catch (e) {
      console.error('PDFのダウンロードに失敗しました:', e)
      showSnackBar(['PDFのダウンロードに失敗しました'], 'error')
      return false
    } finally {
      // 一時的なスタイルを削除
      const styleElement = document.getElementById('pdf-generation-styles')
      if (styleElement) {
        styleElement.remove()
      }

      setIsDownloading(false)
    }
  }

  return {
    isDownloading,
    downloadPdf,
  }
}
